import { Badge, Card, LoadingContainer } from '@/components/ui';
import { useEmpresasAnomaliasNtc } from '@/hooks/useSysadmin';
import type { EmpresaAnomaliaDto } from '@/services/sysadmin.service';
import { AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';

function AnomaliasBadges({ anomalias }: { anomalias: EmpresaAnomaliaDto['anomalias'] }) {
  if (anomalias.length === 0) {
    return (
      <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
        <CheckCircle className="w-3.5 h-3.5" />
        OK
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {anomalias.map((a) => (
        <Badge key={a.ntcId} variant="red">
          {a.nombre}
        </Badge>
      ))}
    </div>
  );
}

export function DiagnosticoNtcPage() {
  const { empresas, isLoading, error, refetch } = useEmpresasAnomaliasNtc();

  if (isLoading) return <LoadingContainer text="Cargando diagnóstico NTC..." />;
  if (error) return <p className="text-red-600 text-center py-8">{error}</p>;

  const conAnomalias = empresas.filter((e) => e.anomalias.length > 0);
  const sinAnomalias = empresas.filter((e) => e.anomalias.length === 0);

  return (
    <div className="space-y-4">
      {/* Summary row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span>
            <strong className="text-gray-900">{empresas.length}</strong> empresas con &gt;3 registros CumplimientoNTC
          </span>
          {conAnomalias.length > 0 && (
            <span className="flex items-center gap-1 text-amber-600 font-medium">
              <AlertTriangle className="w-4 h-4" />
              {conAnomalias.length} con normas fuera de combinación
            </span>
          )}
          {sinAnomalias.length > 0 && (
            <span className="flex items-center gap-1 text-green-600 font-medium">
              <CheckCircle className="w-4 h-4" />
              {sinAnomalias.length} sin anomalías
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={refetch}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Actualizar
        </button>
      </div>

      {empresas.length === 0 ? (
        <Card>
          <div className="py-12 text-center text-gray-500">
            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-3" />
            <p className="font-medium">Sin anomalías</p>
            <p className="text-sm mt-1">No hay empresas con más de 3 registros de cumplimiento NTC.</p>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: '820px' }}>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NIT</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asesor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Combinación actual</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Registros NTC</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Normas fuera de combinación</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {empresas.map((empresa) => (
                  <tr key={empresa.nitId} className={`hover:bg-gray-50 ${empresa.anomalias.length > 0 ? 'bg-amber-50/40' : ''}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{empresa.nombre}</td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{empresa.nitId}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {empresa.asesor ?? <span className="text-gray-400 italic">Sin asesor</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {empresa.combinacion ? (
                        <Badge variant="blue">{empresa.combinacion}</Badge>
                      ) : (
                        <span className="text-gray-400 italic">Sin combinación</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                          empresa.anomalias.length > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {empresa.cumplimientoCount}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <AnomaliasBadges anomalias={empresa.anomalias} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
